import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, Plus, X, CheckCircle2, Clock, AlertTriangle, ArchiveRestore,
  Loader2, Paperclip, Upload, Trash2, User as UserIcon, RefreshCw, Search,
} from "lucide-react";

// ------- Status tone map ---------------------------------------------
const STATUS_TONE = {
  active:              { bg: "bg-[#DCFCE7]", text: "text-[#166534]", label: "Active" },
  awaiting_approval:   { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Awaiting approval" },
  expired:             { bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", label: "Expired" },
  released:            { bg: "bg-[#F1F5F9]", text: "text-[#334155]", label: "Released" },
};

const RECORD_TYPES = [
  { key: "care_seeker", label: "Care Seekers" },
  { key: "clinical_note", label: "Clinical & Wellness Notes" },
  { key: "intake_record", label: "Intake Records" },
  { key: "referral", label: "Referrals" },
  { key: "appointment", label: "Appointments" },
  { key: "attachment", label: "Documents & Attachments" },
  { key: "maternal_visit", label: "Maternal Visits" },
  { key: "care_plan", label: "Care Plans & Goals" },
  { key: "audit_log", label: "Audit Log" },
];

// ------- Create dialog -----------------------------------------------
function CreateHoldDialog({ open, onClose, onCreated }) {
  const [recordTypeKey, setRecordTypeKey] = useState("clinical_note");
  const [reason, setReason] = useState("");
  const [justification, setJustification] = useState("");
  const [expectedEnd, setExpectedEnd] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        record_type_key: recordTypeKey,
        scope: "record_type",
        reason: reason.trim(),
        justification: (justification || reason).trim(),
      };
      if (expectedEnd) payload.expected_end_date = new Date(expectedEnd).toISOString();
      if (reviewDate) payload.review_date = new Date(reviewDate).toISOString();
      const { data } = await api.post("/legal-holds", payload);
      toast.success(`Hold created · ${data.id}`);
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="legal-hold-create-dialog">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#1E3A8A] mt-1" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">Legal hold</div>
            <h2 className="text-lg font-semibold text-slate-900">New enterprise legal hold</h2>
            <p className="text-xs text-slate-500 mt-1">
              Creates the hold in an <span className="font-semibold">Awaiting approval</span> state.
              A second admin must approve before it is officially active. Records under any active
              hold are protected from purges.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800"
            data-testid="legal-hold-create-close"
          ><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">Record type</label>
            <select
              value={recordTypeKey}
              onChange={(e) => setRecordTypeKey(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white"
              data-testid="legal-hold-create-record-type"
            >
              {RECORD_TYPES.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">
              Reason (≥ 10 chars)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Litigation matter #2026-042 · Doe v HarborWell"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
              data-testid="legal-hold-create-reason"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-slate-500">
              Justification
            </label>
            <textarea
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Preservation for subpoena filed 2026-02-25 …"
              className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
              data-testid="legal-hold-create-justification"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-slate-500">Review date</label>
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                data-testid="legal-hold-create-review-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-slate-500">
                Expected end
              </label>
              <input
                type="date"
                value={expectedEnd}
                onChange={(e) => setExpectedEnd(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-[#E5E5E0] rounded-lg"
                data-testid="legal-hold-create-expected-end"
              />
            </div>
          </div>
          <div className="pt-4 border-t border-[#E5E5E0] flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50"
              data-testid="legal-hold-create-submit"
            >
              {saving ? "Creating…" : "Create hold"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm"
            >Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------- Detail drawer -----------------------------------------------
function HoldDetailDrawer({ holdId, onClose, onChange }) {
  const [hold, setHold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [releaseReason, setReleaseReason] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!holdId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/legal-holds/${holdId}`);
      setHold(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [holdId]);
  useEffect(() => { load(); }, [load]);

  if (!holdId) return null;

  const doApprove = async () => {
    if (!window.confirm("Approve this legal hold?\n\nOnce approved, the hold is officially active and cannot be un-approved (only released).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/legal-holds/${holdId}/approve`);
      setHold(data);
      toast.success("Hold approved.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doRelease = async () => {
    if (releaseReason.trim().length < 10) {
      toast.error("Release reason must be at least 10 characters.");
      return;
    }
    if (!window.confirm(`Release this hold?\n\nRecords will re-enter normal retention lifecycle.\nReason: "${releaseReason.trim()}"`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/legal-holds/${holdId}/release`, { reason: releaseReason.trim() });
      setHold(data);
      setReleaseReason("");
      toast.success("Hold released.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("parent_type", "legal_hold");
      fd.append("parent_id", holdId);
      fd.append("file", file);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${file.name}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="legal-hold-detail-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-[#E5E5E0] flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-[#1E3A8A] mt-1" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#1E3A8A]">Legal hold</div>
            <h2 className="text-lg font-semibold text-slate-900 break-all">{holdId}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800"
            data-testid="legal-hold-detail-close"
          ><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" />
          </div>
        ) : hold ? (
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                const tone = STATUS_TONE[hold.status] || STATUS_TONE.active;
                return (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tone.bg} ${tone.text}`}
                        data-testid="legal-hold-detail-status">
                    {tone.label}
                  </span>
                );
              })()}
              <span className="text-xs text-slate-500">
                Created {formatRelative(hold.created_at)} · {hold.created_by_email || "—"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <MetaRow label="Record type" value={hold.record_type_key} />
              <MetaRow label="Scope" value={hold.scope} />
              <MetaRow label="Affected records" value={hold.affected_records ?? "—"} />
              <MetaRow label="Affected modules" value={(hold.affected_modules || []).join(", ") || "—"} />
              <MetaRow label="Review date" value={hold.review_date ? new Date(hold.review_date).toLocaleDateString() : "—"} />
              <MetaRow label="Expected end" value={hold.expected_end_date ? new Date(hold.expected_end_date).toLocaleDateString() : "—"} />
              <MetaRow label="Approved by" value={hold.approved_by_email || "—"} />
              <MetaRow label="Release date" value={hold.release_date ? new Date(hold.release_date).toLocaleDateString() : "—"} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Reason</div>
              <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{hold.reason}</div>
            </div>
            {hold.justification && hold.justification !== hold.reason && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Justification</div>
                <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{hold.justification}</div>
              </div>
            )}

            {/* Attachments — reuse existing pipeline */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  Evidence / Attachments ({hold.attachments_count || 0})
                </div>
                <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-[#E5E5E0] hover:bg-slate-50">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Uploading…" : "Upload"}
                  <input type="file" className="hidden" onChange={doUpload}
                         disabled={uploading}
                         data-testid="legal-hold-attachment-upload" />
                </label>
              </div>
              {(hold.attachments || []).length === 0 ? (
                <p className="text-xs text-slate-500">No documents attached yet.</p>
              ) : (
                <ul className="border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]"
                    data-testid="legal-hold-attachments-list">
                  {hold.attachments.map((a) => (
                    <li key={a.id} className="p-3 text-sm flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-900">{a.filename}</div>
                        <div className="text-xs text-slate-500">
                          {a.content_type} · {Math.round((a.size || 0) / 1024)} KB · uploaded {formatRelative(a.uploaded_at)}
                        </div>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/attachments/${a.id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#1E3A8A] underline"
                      >Download</a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Audit history */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                Audit history ({(hold.audit_history || []).length})
              </div>
              <ul className="border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]"
                  data-testid="legal-hold-audit-history">
                {(hold.audit_history || []).map((e, idx) => (
                  <li key={idx} className="p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-[#1E3A8A]">
                        {e.action}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatRelative(e.ts)} · {e.actor_email || "system"}
                      </span>
                    </div>
                    {e.detail && Object.keys(e.detail).length > 0 && (
                      <pre className="mt-1 text-[11px] text-slate-600 whitespace-pre-wrap font-mono">
                        {JSON.stringify(e.detail, null, 2)}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-[#E5E5E0] space-y-3">
              {hold.status === "awaiting_approval" && (
                <button
                  type="button"
                  onClick={doApprove}
                  disabled={busy}
                  className="w-full px-4 py-2 rounded-lg bg-[#166534] text-white text-sm font-medium hover:bg-[#0f4a24] disabled:opacity-50"
                  data-testid="legal-hold-approve"
                >
                  <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                  Approve (two-person rule — different admin than creator)
                </button>
              )}
              {hold.status !== "released" && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Release reason (≥ 10 chars)"
                    value={releaseReason}
                    onChange={(e) => setReleaseReason(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E5E5E0] rounded-lg text-sm"
                    data-testid="legal-hold-release-reason"
                  />
                  <button
                    type="button"
                    onClick={doRelease}
                    disabled={busy || releaseReason.trim().length < 10}
                    className="w-full px-4 py-2 rounded-lg border border-[#991B1B] text-[#991B1B] text-sm font-medium hover:bg-[#FEE2E2] disabled:opacity-50"
                    data-testid="legal-hold-release"
                  >
                    <ArchiveRestore className="w-4 h-4 inline mr-1.5" />
                    Release hold
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-slate-500">Hold not found.</div>
        )}
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value == null || value === "" ? "—" : String(value)}</div>
    </div>
  );
}

// ------- Main page ---------------------------------------------------
export default function AdminLegalHolds() {
  const [holds, setHolds] = useState([]);
  const [counts, setCounts] = useState({ active: 0, awaiting_approval: 0, expired: 0, released: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [activeHoldId, setActiveHoldId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/legal-holds", { params });
      setHolds(data.holds || []);
      // Counts always come from the unfiltered response too — refetch
      if (filter) {
        const all = await api.get("/legal-holds");
        setCounts(all.data.counts || {});
      } else {
        setCounts(data.counts || {});
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return holds;
    return holds.filter((h) =>
      String(h.id).toLowerCase().includes(q) ||
      String(h.record_type_key).toLowerCase().includes(q) ||
      String(h.reason || "").toLowerCase().includes(q) ||
      String(h.created_by_email || "").toLowerCase().includes(q)
    );
  }, [holds, query]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-16">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        {/* Header */}
        <div className="p-6 rounded-3xl bg-white border border-[#E5E5E0] shadow-sm">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-[#1E3A8A]">
                Enterprise Governance · Legal Holds
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-1">Legal Hold Management</h1>
              <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Enterprise legal-hold registry with two-person approval, expected-end date tracking,
                and enforcement across the retention lifecycle. Records covered by any active hold
                are protected from purges until formally released.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium flex items-center gap-2"
              data-testid="legal-hold-new"
            >
              <Plus className="w-4 h-4" />
              New hold
            </button>
            <button
              type="button"
              onClick={load}
              className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white flex items-center gap-2"
              data-testid="legal-hold-refresh"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Status pills */}
        <div className="mt-6 grid gap-3 md:grid-cols-4">
          {[
            { key: "", label: "All", count: (counts.active || 0) + (counts.awaiting_approval || 0) + (counts.expired || 0) + (counts.released || 0), Icon: ShieldAlert, tone: "text-[#1E3A8A]" },
            { key: "active", label: "Active", count: counts.active || 0, Icon: CheckCircle2, tone: "text-[#166534]" },
            { key: "awaiting_approval", label: "Awaiting approval", count: counts.awaiting_approval || 0, Icon: Clock, tone: "text-[#92400E]" },
            { key: "expired", label: "Expired", count: counts.expired || 0, Icon: AlertTriangle, tone: "text-[#991B1B]" },
          ].map((p) => (
            <button
              key={p.key || "all"}
              type="button"
              onClick={() => setFilter(p.key)}
              className={`p-4 rounded-2xl bg-white border text-left ${
                filter === p.key ? "border-[#1E3A8A] shadow" : "border-[#E5E5E0] hover:border-[#94A3B8]"
              }`}
              data-testid={`legal-hold-filter-${p.key || "all"}`}
            >
              <div className="flex items-center gap-2">
                <p.Icon className={`w-4 h-4 ${p.tone}`} />
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{p.label}</div>
              </div>
              <div className="mt-2 text-3xl font-semibold text-slate-900">{p.count}</div>
            </button>
          ))}
        </div>

        {/* Search + list */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by hold id, record type, reason, or owner…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 px-3 py-2 border border-[#E5E5E0] rounded-lg bg-white text-sm"
              data-testid="legal-hold-search"
            />
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center border border-[#E5E5E0] rounded-2xl bg-white">
              <Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 border border-[#E5E5E0] rounded-2xl bg-white text-center text-sm text-slate-500">
              No holds match the current filter.
            </div>
          ) : (
            <ul className="border border-[#E5E5E0] rounded-2xl divide-y divide-[#E5E5E0] bg-white"
                data-testid="legal-hold-list">
              {filtered.map((h) => {
                const tone = STATUS_TONE[h.status] || STATUS_TONE.active;
                return (
                  <li
                    key={h.id}
                    className="p-4 flex items-center gap-4 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setActiveHoldId(h.id)}
                    data-testid={`legal-hold-row-${h.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold ${tone.bg} ${tone.text}`}>
                          {tone.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                          {h.record_type_key} · {h.scope}
                        </span>
                        {h.attachments_count > 0 && (
                          <span className="text-[10px] text-slate-500 flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            {h.attachments_count}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm font-medium text-slate-900 truncate">{h.reason}</div>
                      <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                        <UserIcon className="w-3 h-3" />
                        {h.created_by_email || "unknown"}
                        · Created {formatRelative(h.created_at)}
                        {h.affected_records != null && (
                          <> · Protects {h.affected_records} record{h.affected_records === 1 ? "" : "s"}</>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-[#1E3A8A]">Open →</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <CreateHoldDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => load()}
      />
      <HoldDetailDrawer
        holdId={activeHoldId}
        onClose={() => setActiveHoldId(null)}
        onChange={load}
      />
    </div>
  );
}
